import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiUrl, mockAuthHeaders } from '../lib/api';

export interface AvailabilityRule {
  id: string;
  personId: string;
  dayOfWeek: number; // 0=Sunday, 6=Saturday
  startTime: string; // "HH:MM"
  endTime: string;   // "HH:MM"
  effectiveFrom: string; // YYYY-MM-DD
  effectiveUntil: string | null;
}

export interface DateSpecificAvailability {
  id: string;
  personId: string;
  date: string; // YYYY-MM-DD
  startTime: string; // "HH:MM"
  endTime: string;   // "HH:MM"
}

export interface PTODay {
  id: string;
  personId: string;
  date: string; // YYYY-MM-DD
  reason: string | null;
}

export interface AvailabilityResponse {
  recurring: AvailabilityRule[];
  dateSpecific: DateSpecificAvailability[];
}

export interface CreateAvailabilityRequest {
  personId: string;
  dayOfWeek: number;
  startTime: string;
  endTime: string;
  effectiveFrom: string;
  effectiveUntil?: string;
}

export interface CreateDateAvailabilityRequest {
  personId: string;
  date: string;
  startTime: string;
  endTime: string;
}

export interface CreatePTORequest {
  personId: string;
  date: string;
  reason?: string;
}

async function fetchAvailability(personId: string): Promise<AvailabilityResponse> {
  const response = await fetch(apiUrl(`/api/v1/people/${personId}/availability`), {
    headers: {
      'Content-Type': 'application/json',
      ...mockAuthHeaders(),
    },
    credentials: 'include',
  });

  if (!response.ok) {
    throw new Error('Failed to fetch availability');
  }

  const json = (await response.json()) as { data?: AvailabilityResponse | AvailabilityRule[] };
  // Handle both old format (array) and new format (object with recurring/dateSpecific)
  if (Array.isArray(json.data)) {
    return { recurring: json.data, dateSpecific: [] };
  }
  return json.data ?? { recurring: [], dateSpecific: [] };
}

async function fetchPTO(personId: string): Promise<PTODay[]> {
  const response = await fetch(apiUrl(`/api/v1/people/${personId}/pto`), {
    headers: {
      'Content-Type': 'application/json',
      ...mockAuthHeaders(),
    },
    credentials: 'include',
  });

  if (!response.ok) {
    throw new Error('Failed to fetch PTO');
  }

  const json = (await response.json()) as { data?: PTODay[] };
  return json.data ?? [];
}

async function createAvailability(data: CreateAvailabilityRequest): Promise<{ id: string }> {
  const response = await fetch(apiUrl(`/api/v1/people/${data.personId}/availability`), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...mockAuthHeaders(),
    },
    credentials: 'include',
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: { message: response.statusText } }));
    throw new Error(error.error?.message ?? 'Failed to create availability rule');
  }

  const json = (await response.json()) as { data?: { id: string } };
  return json.data ?? { id: '' };
}

async function deleteAvailability(personId: string, ruleId: string): Promise<{ deleted: boolean }> {
  const response = await fetch(apiUrl(`/api/v1/people/${personId}/availability/${ruleId}`), {
    method: 'DELETE',
    headers: {
      'Content-Type': 'application/json',
      ...mockAuthHeaders(),
    },
    credentials: 'include',
  });

  if (!response.ok) {
    throw new Error('Failed to delete availability rule');
  }

  const json = (await response.json()) as { data?: { deleted: boolean } };
  return json.data ?? { deleted: false };
}

async function createPTO(data: CreatePTORequest): Promise<{ id: string }> {
  const response = await fetch(apiUrl(`/api/v1/people/${data.personId}/pto`), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...mockAuthHeaders(),
    },
    credentials: 'include',
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: { message: response.statusText } }));
    throw new Error(error.error?.message ?? 'Failed to create PTO');
  }

  const json = (await response.json()) as { data?: { id: string } };
  return json.data ?? { id: '' };
}

async function deletePTO(personId: string, ptoId: string): Promise<{ deleted: boolean }> {
  const response = await fetch(apiUrl(`/api/v1/people/${personId}/pto/${ptoId}`), {
    method: 'DELETE',
    headers: {
      'Content-Type': 'application/json',
      ...mockAuthHeaders(),
    },
    credentials: 'include',
  });

  if (!response.ok) {
    throw new Error('Failed to delete PTO');
  }

  const json = (await response.json()) as { data?: { deleted: boolean } };
  return json.data ?? { deleted: false };
}

async function createDateAvailability(data: CreateDateAvailabilityRequest): Promise<{ id: string }> {
  const response = await fetch(apiUrl(`/api/v1/people/${data.personId}/availability/date`), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...mockAuthHeaders(),
    },
    credentials: 'include',
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: { message: response.statusText } }));
    throw new Error(error.error?.message ?? 'Failed to create date-specific availability');
  }

  const json = (await response.json()) as { data?: { id: string } };
  return json.data ?? { id: '' };
}

async function deleteDateAvailability(personId: string, date: string): Promise<{ deleted: boolean }> {
  const response = await fetch(apiUrl(`/api/v1/people/${personId}/availability/date/${date}`), {
    method: 'DELETE',
    headers: {
      'Content-Type': 'application/json',
      ...mockAuthHeaders(),
    },
    credentials: 'include',
  });

  if (!response.ok) {
    throw new Error('Failed to delete date-specific availability');
  }

  const json = (await response.json()) as { data?: { deleted: boolean } };
  return json.data ?? { deleted: false };
}

export function useAvailability(personId: string | undefined) {
  return useQuery({
    queryKey: ['availability', personId],
    queryFn: () => fetchAvailability(personId!),
    enabled: !!personId,
    staleTime: 30000,
  });
}

export function usePTO(personId: string | undefined) {
  return useQuery({
    queryKey: ['pto', personId],
    queryFn: () => fetchPTO(personId!),
    enabled: !!personId,
    staleTime: 30000,
  });
}

export function useCreateAvailability() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createAvailability,
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['availability', variables.personId] });
    },
  });
}

export function useDeleteAvailability(personId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (ruleId: string) => deleteAvailability(personId, ruleId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['availability', personId] });
    },
  });
}

export function useCreatePTO() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createPTO,
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['pto', variables.personId] });
    },
  });
}

export function useDeletePTO(personId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (ptoId: string) => deletePTO(personId, ptoId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pto', personId] });
    },
  });
}

export function useCreateDateAvailability() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createDateAvailability,
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['availability', variables.personId] });
    },
  });
}

export function useDeleteDateAvailability(personId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (date: string) => deleteDateAvailability(personId, date),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['availability', personId] });
    },
  });
}
